/**
 * Dados e textos comuns das telas "Meus alertas", detalhe do alerta e contestação.
 * Tudo vem da API de hoje (`/violations`, `/rules`, `/violation-reports`).
 */
import { useQuery } from '@tanstack/react-query'

import { type ApiHttpError, extrairDetail } from '@/lib/api'

import { listarRegras, type RegraCatalogo } from '@/api/regras'
import { listarViolacoes, type Violacao } from '@/api/violacoes'

/** Prefixo das chaves de cache destas telas ("Atualizar" limpa tudo de uma vez). */
export const CHAVE_MEUS_ALERTAS = 'meus-alertas'

/** Tamanho da página do `GET /violations` (máximo aceito pela API). */
export const LIMITE_LISTA = 500

// ── Textos ────────────────────────────────────────────────────────────────

const TIPOS: Record<string, { curto: string; nome: string; artigo: 'a' | 'o' }> = {
  merge_request: { curto: 'MR', nome: 'merge request', artigo: 'a' },
  issue: { curto: 'Issue', nome: 'issue', artigo: 'a' },
  epic: { curto: 'Épico', nome: 'épico', artigo: 'o' },
  milestone: { curto: 'Milestone', nome: 'milestone', artigo: 'a' },
  wiki: { curto: 'Wiki', nome: 'página de wiki', artigo: 'a' },
  push: { curto: 'Push', nome: 'push', artigo: 'o' },
  tag: { curto: 'Tag', nome: 'tag', artigo: 'a' },
}

/** Nome curto do tipo do item (ex.: `merge_request` → "MR"). */
export function tipoCurto(tipo: string): string {
  return TIPOS[tipo]?.curto ?? tipo
}

/** Nome do tipo com artigo, para frases (ex.: "a merge request"). */
export function tipoComArtigo(tipo: string): string {
  const t = TIPOS[tipo]
  return t ? `${t.artigo} ${t.nome}` : `o item (${tipo})`
}

/** Tipo com "em" na frente, para frases (ex.: "na merge request", "no épico"). */
export function noTipo(tipo: string): string {
  return tipoComArtigo(tipo).replace(/^(a|o) /, 'n$1 ')
}

/** Número do item como o GitLab escreve: `!12` para MR, `#12` para o resto. */
export function referencia(alerta: Pick<Violacao, 'resource_type' | 'resource_iid'>): string {
  return `${alerta.resource_type === 'merge_request' ? '!' : '#'}${alerta.resource_iid}`
}

/** Nome do projeto para mostrar (nome curto, caminho ou o id do GitLab). */
export function nomeProjeto(alerta: Violacao): string {
  return (
    (alerta.project_name ?? '').trim() ||
    (alerta.project_path_with_namespace ?? '').trim() ||
    `GitLab ID ${alerta.gitlab_project_id}`
  )
}

/** Link do item no GitLab (MR ou work item). */
export function urlNoGitlab(alerta: Violacao): string {
  const caminho = (alerta.project_path_with_namespace ?? '').trim()
  // Sem o caminho, abre o projeto pelo id (o GitLab redireciona /projects/<id>).
  if (!caminho) return `https://gitlab.com/projects/${alerta.gitlab_project_id}`
  return alerta.resource_type === 'merge_request'
    ? `https://gitlab.com/${caminho}/-/merge_requests/${alerta.resource_iid}`
    : `https://gitlab.com/${caminho}/-/work_items/${alerta.resource_iid}`
}

const CATEGORIAS: Record<string, string> = {
  branch: 'Branches',
  bug: 'Bugs',
  dor: 'Pronto para desenvolver',
  duplicates: 'Duplicadas',
  epic: 'Épicos',
  hotfix: 'Hotfix',
  issue: 'Issues',
  labels: 'Labels',
  milestone: 'Milestones',
  mr: 'Merge requests',
  tag: 'Tags',
  workflow: 'Fluxo de trabalho',
}

/** Nome da categoria de regra em português claro. */
export function nomeCategoria(categoria: string): string {
  return CATEGORIAS[categoria] ?? categoria
}

// ── Contestação ───────────────────────────────────────────────────────────

/** Tamanho mínimo do texto da contestação. */
export const TAMANHO_MINIMO_MENSAGEM = 20

/** Mensagem de um erro da API (o `detail`), com o texto padrão quando não vier nenhum. */
export function detalheDaApi(erro: ApiHttpError): string {
  const detalhe = erro.detail
  if (detalhe === null || detalhe === undefined || detalhe === '') {
    return 'Não foi possível concluir a operação. Tente novamente.'
  }
  return extrairDetail(erro)
}

// ── Datas ─────────────────────────────────────────────────────────────────

/** Instante da API; sem fuso no texto, vale como UTC. */
export function lerInstante(iso: string): Date {
  const temFuso = /(z|[+-]\d{2}(:?\d{2})?)$/i.test(iso.trim())
  return new Date(temFuso ? iso : `${iso}Z`)
}

const FORMATO_DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/** `dd/mm/aaaa hh:mm`, no fuso de São Paulo. */
export function dataHora(iso: string): string {
  const data = lerInstante(iso)
  if (Number.isNaN(data.getTime())) return iso
  return FORMATO_DATA_HORA.format(data).replace(',', '')
}

const RELATIVO = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })

/** Quanto tempo faz, em português ("há 2 horas", "ontem", "há 5 dias"). */
export function haQuantoTempo(iso: string, agora: Date = new Date()): string {
  const data = lerInstante(iso)
  if (Number.isNaN(data.getTime())) return iso
  const minutos = Math.round((agora.getTime() - data.getTime()) / 60_000)
  if (minutos < 1) return 'agora'
  if (minutos < 60) return RELATIVO.format(-minutos, 'minute')
  const horas = Math.round(minutos / 60)
  if (horas < 24) return RELATIVO.format(-horas, 'hour')
  const dias = Math.round(horas / 24)
  if (dias < 30) return RELATIVO.format(-dias, 'day')
  return dataHora(iso).slice(0, 10)
}

/** `AAAA-MM-DD` de uma data local. */
export function isoDoDia(data: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${data.getFullYear()}-${p(data.getMonth() + 1)}-${p(data.getDate())}`
}

/** `AAAA-MM-DD` → data local à meia-noite. */
export function diaDoIso(iso: string): Date {
  const [ano, mes, dia] = iso.split('-').map(Number)
  return new Date(ano ?? 1970, (mes ?? 1) - 1, dia ?? 1)
}

/** `AAAA-MM-DD` → `dd/mm/aaaa`. */
export function diaBr(iso: string): string {
  const [ano, mes, dia] = iso.split('-')
  return `${dia}/${mes}/${ano}`
}

/** Hoje menos `dias`, em `AAAA-MM-DD`. */
export function diasAtras(dias: number): string {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  return isoDoDia(d)
}

// ── Consultas ─────────────────────────────────────────────────────────────

/** Catálogo de regras (nome e descrição de cada código). */
export function useCatalogoRegras() {
  return useQuery({
    queryKey: [CHAVE_MEUS_ALERTAS, 'catalogo'],
    queryFn: listarRegras,
    staleTime: 5 * 60_000,
  })
}

/** Código da regra → item do catálogo. */
export function mapaDoCatalogo(regras: RegraCatalogo[] | undefined): Map<string, RegraCatalogo> {
  return new Map((regras ?? []).map((regra) => [regra.code, regra]))
}

/** Quantas páginas de 500 o detalhe percorre procurando o alerta (até 5 mil alertas). */
const PAGINAS_NA_BUSCA = 10

/**
 * Um alerta da pessoa pelo id. A API não tem `GET /violations/{id}`: a busca
 * percorre a lista dela (mais recentes primeiro) e devolve também os outros
 * alertas do mesmo item (mesma MR ou issue).
 */
export async function buscarAlertaDaPessoa(
  autor: string,
  id: number
): Promise<{ alerta: Violacao; doMesmoItem: Violacao[] } | null> {
  for (let pagina = 0; pagina < PAGINAS_NA_BUSCA; pagina += 1) {
    const lista = await listarViolacoes(
      { author_username: autor },
      LIMITE_LISTA,
      pagina * LIMITE_LISTA
    )
    const alerta = lista.find((item) => item.id === id)
    if (alerta) {
      const doMesmoItem = lista.filter(
        (item) =>
          item.id !== alerta.id &&
          item.gitlab_project_id === alerta.gitlab_project_id &&
          item.resource_type === alerta.resource_type &&
          item.resource_iid === alerta.resource_iid
      )
      return { alerta, doMesmoItem }
    }
    if (lista.length < LIMITE_LISTA) break
  }
  return null
}
